import type { JSX } from 'react';
import { Alert, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IOsasReferralData } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import type { IReduxState } from '@frontend/configureStore';
import { ServiceCode } from '@common/enums';
import { CustomControlMode } from '@common/enums/serviceWorkflow/customControlMode';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { SleepRecorderType } from '@common/validation/customControls/osas/sleepRecorderType';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { CareProviderSelector } from '@frontend/components/pages/customControls/utils/v1/CareProviderSelector';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch, useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { BmiFormGroup } from '../../BmiProcessActivityFormGroupFields';
import { PatientContactDetailsFormSection } from '../../patientContactDetails/PatientContactDetailsProcessActivityFormSection';
import { PatientInsurance } from '../../patientInsurance/PatientInsurance';
import { ProcessActivityAssetsDropzone } from '../../processActivityAssets/ProcessActivityAssetsDropzone';
import { TextInputFormGroup } from '../../processActivityFormGroupFields';
import { ProcessActivityMedicationsAddEdit } from '../../processActivityMedications/ProcessActivityMedicationsAddEdit';
import { ProcessActivityProfessionalSummaryDataAddEdit } from '../../processActivityProfessionalSummaryData/ProcessActivityProfessionalSummaryDataAddEdit';
import { AlcoholConsumptionFormGroup } from './components/AlcoholConsumptionFormGroup';
import { ComorbiditiesFormGroup } from './components/ComorbiditiesFormGroup';
import { ContraIndicationsOsas } from './components/ContraIndicationsOsas';
import { DrugUseFormGroup } from './components/DrugUseFormGroup';
import { ImportantComplaintsFormGroup } from './components/ImportantComplaintsFormGroup';
import { OsasFollowingSteps } from './components/osasFollowingSteps';
import { PhysicalExaminationFormSection } from './components/PhysicalExaminationFormSection';
import { SleepRecorderTypeFormGroup } from './components/SleepRecorderTypeFormGroup';
import { SmokingFormGroup } from './components/SmokingFormGroup';

export function OsasReferralEdit(): JSX.Element {
    const { processActivityId, errors, data, handleCancel, onChange } =
        useProcessActivityEditContext<IOsasReferralData>();
    const abortSignal = useAbortSignal();
    const abortableFetch = useAbortableFetch();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [canReferPatientWithoutSmartphone, setCanReferPatientWithoutSmartphone] = useState(false);
    const { patient } = usePatientEpisodeCategoryContext();
    const [isHighPrivacy, setIsHighPrivacy] = useState(false);
    const translate = getSelectedLanguage();

    useEffect(() => {
        fillDefaultSleepRecorderTypeDetails();
        getHighPrivacySetting();
    }, []);

    useEffect(() => {
        if (!data.patient.identifierSocialSecurity && patient?.identifierSocialSecurity) {
            onChange(
                {
                    patient: {
                        ...data.patient,
                        identifierSocialSecurity: patient.identifierSocialSecurity,
                    },
                },
                'patient.identifierSocialSecurity'
            );
        }
    }, [patient.identifierSocialSecurity]);

    useEffect(() => {
        if (!data.draftCareProviderOrganizationId && !data.draftCareProviderPractitionerId) {
            onChange({
                draftCareProviderOrganizationId: patient.careProviderOrganizationId,
                draftCareProviderPractitionerId: patient.careProviderPractitionerId,
            });
        }
    }, [patient]);

    return (
        <>
            <PatientInsurance
                isPatientInformed={data.isPatientInformed}
                mode={CustomControlMode.edit}
                onCancel={handleCancel}
                onChange={(params) => onChange(params, 'isPatientInformed')}
                serviceCode={ServiceCode.OSAS}
            />
            <ContraIndicationsOsas />
            <SleepRecorderTypeFormGroup
                canReferPatientWithoutSmartphone={canReferPatientWithoutSmartphone}
                data={data}
                errors={errors}
                onChange={onChange}
            />
            {(canReferPatientWithoutSmartphone ||
                data.sleepRecorderType === SleepRecorderType.recorderWithSmartphone) && (
                <>
                    {showIdentifierSocialSecurityField() && (
                        <TextInputFormGroup
                            fieldKey="patient.identifierSocialSecurity"
                            isRequired={true}
                            label={`${translate.patient.identifierSocialSecurityLong} (${translate.patient.identifierSocialSecurity})`}
                            placeholder={translate.patient.identifierSocialSecurityLong}
                        />
                    )}

                    <CareProviderSelector
                        contextPatient={patient}
                        entity={{
                            draftCareProviderOrganizationId:
                                data.draftCareProviderOrganizationId ?? patient.careProviderOrganizationId ?? null,
                            draftCareProviderPractitionerId:
                                data.draftCareProviderPractitionerId ?? patient.careProviderPractitionerId ?? null,
                        }}
                        errors={errors}
                        handleChange={(name, value) => onChange({ [name]: value }, name)}
                        isHighPrivacy={isHighPrivacy}
                        validateOne={() => {}}
                    />

                    <PatientContactDetailsFormSection
                        alert={
                            <Alert appearance="info">
                                {patient.country === 'NL' || !patient.country
                                    ? 'Let op: de slaaprecorder kan alleen verstuurd worden naar een adres binnen Nederland. De patiënt ontvangt na het versturen een track & trace e-mail.'
                                    : 'De slaaprecorder wordt opgestuurd naar onderstaand adres. De patiënt ontvangt hiervan een track & trace e-mail.'}
                            </Alert>
                        }
                    />

                    <FormSection title="Verwijzing">
                        <FormParagraphFormGroup label="Verstuur naar">Ksyos Ziekenhuis</FormParagraphFormGroup>
                        <TextInputFormGroup
                            fieldKey="referralReason"
                            isRequired={true}
                            label="Verwijsreden"
                            placeholder="Verwijsreden"
                        />
                        <ImportantComplaintsFormGroup
                            data={data}
                            errors={errors}
                            isRequired={true}
                            onChange={onChange}
                        />
                        <ComorbiditiesFormGroup
                            data={data}
                            errors={errors}
                            isRequired={true}
                            onChange={onChange}
                        />
                    </FormSection>

                    <ProcessActivityMedicationsAddEdit
                        errors={errors}
                        onChange={onChange}
                        patientUsesMedication={data.patientUsesMedication}
                        processActivityId={processActivityId}
                    />

                    <ProcessActivityProfessionalSummaryDataAddEdit
                        onChange={onChange}
                        processActivityId={processActivityId}
                    />

                    <PhysicalExaminationFormSection
                        data={data}
                        errors={errors}
                        onChange={onChange}
                    />

                    <FormSection title="Leefstijl">
                        <FormSection isBorderless={true}>
                            <BmiFormGroup
                                errors={errors}
                                fieldKey={{ height: 'lengthInCm', weight: 'weightInKg' }}
                                height={data.lengthInCm}
                                isRequired={true}
                                weight={data.weightInKg}
                            />
                        </FormSection>
                        <FormSection isBorderless={true}>
                            <AlcoholConsumptionFormGroup
                                data={data}
                                errors={errors}
                                onChange={onChange}
                            />
                        </FormSection>
                        <FormSection isBorderless={true}>
                            <SmokingFormGroup
                                data={data}
                                errors={errors}
                                onChange={onChange}
                            />
                        </FormSection>
                        <FormSection isBorderless={true}>
                            <DrugUseFormGroup
                                data={data}
                                errors={errors}
                                onChange={onChange}
                            />
                        </FormSection>
                    </FormSection>

                    <FormSection title="Relevante onderzoeken">
                        <ProcessActivityAssetsDropzone
                            label="Upload eventuele eerdere slaap- of bloedonderzoeken"
                            maxFiles={20}
                        />
                    </FormSection>

                    <OsasFollowingSteps />
                </>
            )}
        </>
    );

    async function fillDefaultSleepRecorderTypeDetails() {
        const urlBuilder = new UrlBuilder('/api/serviceOrganizationSettings');
        urlBuilder.add('serviceCode', ServiceCode.OSAS);
        urlBuilder.add('organizationId', organizationId);
        urlBuilder.add('isActive', true);
        const url = urlBuilder.build();

        const canReferPatientSansSmartphone = (await abortableFetch.fetchJson(url, 'GET'))?.json?.items?.[0]
            ?.canReferPatientWithoutSmartphone;
        setCanReferPatientWithoutSmartphone(canReferPatientSansSmartphone);
    }

    function showIdentifierSocialSecurityField() {
        return (
            !patient?.identifierSocialSecurity ||
            errors.has('patient.identifierSocialSecurity') ||
            patient?.identifierSocialSecurity !== data.patient.identifierSocialSecurity
        );
    }

    async function getHighPrivacySetting() {
        const organization = await getOneOrganization(organizationId, abortSignal);
        setIsHighPrivacy(Boolean(organization?.isHighPrivacy));
    }
}
